import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Search, X, Plus, CheckSquare, Square, ArrowLeft, Save, Phone, AlertTriangle, User } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ── Missing fields check (2nd call rule) ─────────────────────────────────────
const getMissingFields = (account, isFirstCall = false) => {
  const missing = [];
  if (!account.city) missing.push('City');
  if (!isFirstCall && !account.clinic_potential) missing.push('Clinic Potential');
  return missing;
};

// ── Constants ─────────────────────────────────────────────────────────────────
const PURPOSES = [
  'Product Promotion',
  'Case Follow-Up',
  'Complaint Resolution',
  'Webinar/Workshop Invite',
  'Post Webinar/Workshop Follow-Up',
  'Others',
];

const COMPETITOR_OPTIONS = ['Invisalign', 'Ormco Spark', 'Dentcare Aligners', 'Smile Aligners', 'Illusion Aligners'];

const FOLLOWUP_ACTIONS = [
  'Schedule an in-person meeting by the representative',
  'Do a follow-up call post discussion with Orthodontist',
  'Share online resources / pricing with the doctor',
  'Connect doctor to Orthodontist for concern resolution',
  'Do a follow-up call at a later date and time',
];

const FOLLOWUP_DATE_OPTIONS = ['In 7 Days', 'In 14 Days', 'Custom Date', 'Assign to Representative'];
const WORKSHOP_RESPONSES = ['Interested', 'Not Interested', 'Maybe — Need Follow-Up'];

// ── Helper Components ─────────────────────────────────────────────────────────
const Chip = ({ label, selected, onClick, color = 'teal', fullWidth = false }) => {
  const colors = {
    teal: selected ? 'bg-teal-700 text-white border-teal-700 shadow-md' : 'bg-white text-slate-600 border-slate-200 hover:border-teal-400',
    emerald: selected ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-slate-600 border-slate-200 hover:border-emerald-400',
    red: selected ? 'bg-red-500 text-white border-red-500' : 'bg-white text-slate-600 border-slate-200 hover:border-red-400',
    amber: selected ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-slate-600 border-slate-200 hover:border-amber-400',
  };
  return (
    <button type="button" onClick={onClick}
      className={`${fullWidth ? 'w-full' : ''} px-4 py-3 rounded-xl border-2 text-sm font-semibold transition-all active:scale-95 ${colors[color]}`}>
      {label}
    </button>
  );
};

const Section = ({ step, title, subtitle, children }) => (
  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
    <div className="flex items-start gap-3 mb-4">
      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-700 to-emerald-500 flex items-center justify-center flex-shrink-0 mt-0.5">
        <span className="text-white text-xs font-bold">{step}</span>
      </div>
      <div>
        <h2 className="text-base font-bold text-slate-800">{title}</h2>
        {subtitle && <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>}
      </div>
    </div>
    {children}
  </div>
);

const Checklist = ({ options, selected, onChange, othersText, onOthersChange }) => {
  const toggle = opt => onChange(selected.includes(opt) ? selected.filter(x => x !== opt) : [...selected, opt]);
  return (
    <div className="space-y-2">
      {options.map(opt => (
        <button key={opt} type="button" onClick={() => toggle(opt)}
          className="flex items-center gap-3 w-full text-left px-3 py-3 rounded-xl border border-slate-200 hover:border-teal-300 active:bg-teal-50 transition-all">
          {selected.includes(opt) ? <CheckSquare className="h-5 w-5 text-teal-600 flex-shrink-0" /> : <Square className="h-5 w-5 text-slate-300 flex-shrink-0" />}
          <span className="text-sm text-slate-700">{opt}</span>
        </button>
      ))}
      {onOthersChange && (
        <div>
          <button type="button" onClick={() => toggle('Others')}
            className="flex items-center gap-3 w-full text-left px-3 py-3 rounded-xl border border-slate-200 hover:border-teal-300 transition-all">
            {selected.includes('Others') ? <CheckSquare className="h-5 w-5 text-teal-600 flex-shrink-0" /> : <Square className="h-5 w-5 text-slate-300 flex-shrink-0" />}
            <span className="text-sm text-slate-700">Others</span>
          </button>
          {selected.includes('Others') && (
            <Input value={othersText} onChange={e => onOthersChange(e.target.value)}
              placeholder="Please specify..." className="mt-2 rounded-xl" />
          )}
        </div>
      )}
    </div>
  );
};

const CallExperience = ({ value, onChange }) => {
  const emojis = ['😟', '😐', '🙂', '😊', '😄'];
  const labels = ['Poor', 'Below Avg', 'Average', 'Good', 'Excellent'];
  return (
    <div className="flex gap-2">
      {[1, 2, 3, 4, 5].map(n => (
        <button key={n} type="button" onClick={() => onChange(n)}
          className={`flex-1 flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition-all ${
            value === n ? 'border-teal-500 bg-teal-50' : 'border-slate-200 hover:border-teal-300'
          }`}>
          <span className="text-2xl">{emojis[n - 1]}</span>
          <span className="text-xs font-medium text-slate-500">{n}</span>
          <span className="text-xs text-slate-400 hidden sm:block">{labels[n - 1]}</span>
        </button>
      ))}
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
const CreateTelephonicCall = ({ onLogout }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const searchRef = useRef(null);

  // Step 1 — Date
  const [callDate, setCallDate] = useState(new Date().toISOString().slice(0, 10));

  // Step 2 — Account mode
  const [accountMode, setAccountMode] = useState(''); // 'existing' | 'new'

  // Existing account
  const [accountSearch, setAccountSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searching, setSearching] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [contactPersonId, setContactPersonId] = useState('');

  // New contact
  const [newPhone, setNewPhone] = useState('');
  const [duplicateAccount, setDuplicateAccount] = useState(null);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);
  const [duplicateClinic, setDuplicateClinic] = useState(null);
  const [checkingClinicDuplicate, setCheckingClinicDuplicate] = useState(false);
  const [newDoctorName, setNewDoctorName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newCity, setNewCity] = useState('');

  // Step 3 — Purpose
  const [purposes, setPurposes] = useState([]);
  const [purposesOthers, setPurposesOthers] = useState('');

  // Product Promotion
  const [promoProducts, setPromoProducts] = useState([]);
  const [promoHigherInterest, setPromoHigherInterest] = useState([]);

  // Complaint
  const [complaintProducts, setComplaintProducts] = useState([]);
  const [complaintClosed, setComplaintClosed] = useState('');

  // Workshop Invite
  const [workshopInviteName, setWorkshopInviteName] = useState('');
  const [workshopInviteDate, setWorkshopInviteDate] = useState('');
  const [workshopInviteResponse, setWorkshopInviteResponse] = useState('');

  // Post Workshop
  const [postWorkshopName, setPostWorkshopName] = useState('');
  const [postWorkshopDate, setPostWorkshopDate] = useState('');
  const [postWorkshopProducts, setPostWorkshopProducts] = useState([]);
  const [postWorkshopHigher, setPostWorkshopHigher] = useState([]);

  // Follow-up & experience
  const [followUpActions, setFollowUpActions] = useState([]);
  const [followUpOthers, setFollowUpOthers] = useState('');
  const [callExperience, setCallExperience] = useState('');

  // Create account inline
  const [createAccount, setCreateAccount] = useState(''); // 'yes' | 'no'
  const [clinicName, setClinicName] = useState('');
  const [clinicAddress, setClinicAddress] = useState('');
  const [clinicCity, setClinicCity] = useState('');
  const [clinicPincode, setClinicPincode] = useState('');
  const [clinicPatientInflow, setClinicPatientInflow] = useState('');
  const [clinicCompetitors, setClinicCompetitors] = useState([]);
  const [accountCreated, setAccountCreated] = useState(false);
  const [creatingAccount, setCreatingAccount] = useState(false);

  // Follow-up date & notes
  const [followUpDate, setFollowUpDate] = useState('');
  const [customDate, setCustomDate] = useState('');
  const [personalNotes, setPersonalNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Missing fields (existing account 2nd call check)
  const [missingFields, setMissingFields] = useState([]);
  const [inlinePotential, setInlinePotential] = useState('');
  const [inlineCity, setInlineCity] = useState('');
  const [savingMissing, setSavingMissing] = useState(false);
  const [showNewContactModal, setShowNewContactModal] = useState(false);
  const [newContactForm, setNewContactForm] = useState({ name: '', designation: '', whatsapp_number: '', email: '', can_influence_decisions: '' });
  const [addingContact, setAddingContact] = useState(false);

  // Products master
  const [allProducts, setAllProducts] = useState([]);

  useEffect(() => { fetchProducts(); }, []);

  useEffect(() => {
    const handler = e => { if (searchRef.current && !searchRef.current.contains(e.target)) setShowSearchResults(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (accountSearch.length < 2) { setSearchResults([]); setShowSearchResults(false); return; }
    if (selectedAccount) return;
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const token = localStorage.getItem('token');
        const r = await axios.get(`${API}/accounts/search?q=${encodeURIComponent(accountSearch)}`, { headers: { Authorization: `Bearer ${token}` } });
        setSearchResults(r.data); setShowSearchResults(true);
      } catch {} finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [accountSearch, selectedAccount]);

  // Duplicate phone check
  useEffect(() => {
    if (newPhone.replace(/[^0-9]/g, '').length < 10) { setDuplicateAccount(null); return; }
    const t = setTimeout(async () => {
      setCheckingDuplicate(true);
      try {
        const token = localStorage.getItem('token');
        const r = await axios.get(`${API}/accounts/search?q=${newPhone}`, { headers: { Authorization: `Bearer ${token}` } });
        const match = r.data.find(a => a.contacts?.some(c => c.whatsapp_number?.replace(/[^0-9]/g, '').includes(newPhone.replace(/[^0-9]/g, '').slice(-10))));
        setDuplicateAccount(match || null);
      } catch {} finally { setCheckingDuplicate(false); }
    }, 500);
    return () => clearTimeout(t);
  }, [newPhone]);

  // Clinic name duplicate check
  useEffect(() => {
    if (!clinicName || clinicName.length < 3) { setDuplicateClinic(null); return; }
    const t = setTimeout(async () => {
      setCheckingClinicDuplicate(true);
      try {
        const token = localStorage.getItem('token');
        const r = await axios.get(`${API}/accounts/search?q=${encodeURIComponent(clinicName)}`, { headers: { Authorization: `Bearer ${token}` } });
        const match = r.data.find(a => a.name.toLowerCase().includes(clinicName.toLowerCase()) || clinicName.toLowerCase().includes(a.name.toLowerCase().slice(0, 5)));
        setDuplicateClinic(match || null);
      } catch {} finally { setCheckingClinicDuplicate(false); }
    }, 500);
    return () => clearTimeout(t);
  }, [clinicName]);

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API}/products`, { headers: { Authorization: `Bearer ${token}` } });
      setAllProducts(r.data);
    } catch {}
  };

  const handleSelectAccount = async (account) => {
    setAccountSearch(account.name);
    setShowSearchResults(false);
    try {
      const token = localStorage.getItem('token');
      const [freshRes, callsRes] = await Promise.all([
        axios.get(`${API}/accounts/${account.id}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/accounts/${account.id}/sales-history`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
      ]);
      const fresh = freshRes.data;
      const isFirstCall = !callsRes.data?.length;
      setSelectedAccount(fresh);
      setMissingFields(getMissingFields(fresh, isFirstCall));
    } catch {
      setSelectedAccount(account);
      setMissingFields([]);
    }
  };

  const handleAddNewContact = async () => {
    if (!newContactForm.name.trim()) { toast.error('Name required'); return; }
    if (!newContactForm.designation) { toast.error('Designation required'); return; }
    if (!newContactForm.whatsapp_number.trim()) { toast.error('Phone required'); return; }
    if (!newContactForm.can_influence_decisions) { toast.error('Set influence level'); return; }
    setAddingContact(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/accounts/${selectedAccount.id}/contacts`,
        { ...newContactForm, email: newContactForm.email || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const fresh = await axios.get(`${API}/accounts/${selectedAccount.id}`, { headers: { Authorization: `Bearer ${token}` } });
      setSelectedAccount(fresh.data);
      setNewContactForm({ name: '', designation: '', whatsapp_number: '', email: '', can_influence_decisions: '' });
      setShowNewContactModal(false);
      toast.success('Contact added! ✅');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setAddingContact(false); }
  };

  const handleSaveMissingFields = async () => {
    if (missingFields.includes('Clinic Potential') && !inlinePotential) { toast.error('Please select Clinic Potential'); return; }
    setSavingMissing(true);
    try {
      const token = localStorage.getItem('token');
      const updates = {};
      if (inlinePotential) updates.clinic_potential = inlinePotential;
      if (inlineCity.trim()) updates.city = inlineCity.trim();
      await axios.put(`${API}/accounts/${selectedAccount.id}/fill-missing-fields`, updates, { headers: { Authorization: `Bearer ${token}` } });
      const fresh = await axios.get(`${API}/accounts/${selectedAccount.id}`, { headers: { Authorization: `Bearer ${token}` } });
      setSelectedAccount(fresh.data);
      setMissingFields([]);
      setInlinePotential('');
      setInlineCity('');
      toast.success('Account details saved! ✅');
    } catch { toast.error('Failed to save'); }
    finally { setSavingMissing(false); }
  };

  const handleCreateAccountInline = async () => {
    if (!clinicName) { toast.error('Clinic name is required'); return; }
    if (!clinicAddress) { toast.error('Clinic address is required'); return; }
    if (!newDoctorName) { toast.error('Doctor name is required'); return; }
    setCreatingAccount(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      // Step 1: Create the account with correct payload
      const accountPayload = {
        name: clinicName,
        address: clinicAddress,
        city: clinicCity || newCity || '',
        pincode: clinicPincode || '',
        clinic_potential: clinicPatientInflow || '',
        competitor_brands: [],
        competitor_brands_others: '',
        contacts: [{
          id: Date.now().toString(),
          name: newDoctorName,
          designation: 'Clinic Owner (Orthodontist)',
          whatsapp_number: newPhone,
          email: newEmail || null,
          can_influence_decisions: '',
        }],
        current_sales_value: 0,
      };
      const accRes = await axios.post(`${API}/accounts`, accountPayload, { headers });
      const newAccount = accRes.data;
      setSelectedAccount(newAccount);
      setAccountCreated(true);
      toast.success('Account created! Now save the call. ✅');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create account'); }
    finally { setCreatingAccount(false); }
  };

  const getFollowUpDate = () => {
    if (followUpDate === 'Custom Date') return customDate || null;
    if (followUpDate === 'In 7 Days') { const d = new Date(); d.setDate(d.getDate() + 7); return d.toISOString().slice(0, 10); }
    if (followUpDate === 'In 14 Days') { const d = new Date(); d.setDate(d.getDate() + 14); return d.toISOString().slice(0, 10); }
    if (followUpDate === 'Assign to Representative') return 'assign-to-rep';
    return null;
  };

  const isNewContactReady = accountMode === 'new' && newPhone && newDoctorName;
  const isExistingReady = accountMode === 'existing' && selectedAccount;
  const accountReady = isNewContactReady || isExistingReady;
  const purposesReady = purposes.length > 0;

  const hasPromo = purposes.includes('Product Promotion');
  const hasComplaint = purposes.includes('Complaint Resolution');
  const hasWorkshopInvite = purposes.includes('Webinar/Workshop Invite');
  const hasPostWorkshop = purposes.includes('Post Webinar/Workshop Follow-Up');

  // Check all required purpose sections filled
  const purposeSectionsDone = () => {
    if (hasPromo && promoProducts.length === 0) return false;
    if (hasComplaint && (complaintProducts.length === 0 || !complaintClosed)) return false;
    if (hasWorkshopInvite && (!workshopInviteName || !workshopInviteDate || !workshopInviteResponse)) return false;
    if (hasPostWorkshop && (!postWorkshopName || !postWorkshopDate)) return false;
    return true;
  };

  const handleSubmit = async () => {
    if (!accountReady) { toast.error('Please select or enter a contact'); return; }
    if (!purposesReady) { toast.error('Please select at least one purpose'); return; }
    if (!purposeSectionsDone()) { toast.error('Please complete all required fields for selected purposes'); return; }
    if (followUpActions.length === 0) { toast.error('Please select at least one follow-up action'); return; }
    if (!callExperience) { toast.error('Please rate the call experience'); return; }
    if (!followUpDate) { toast.error('Please select a follow-up date'); return; }
    if (followUpDate === 'Custom Date' && !customDate) { toast.error('Please enter a custom date'); return; }
    if (accountMode === 'new' && !accountCreated && !createAccount) {
      toast.error('Please choose: Create Account or Save as Lead');
      window.scrollTo(0, 400);
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        call_date: callDate,
        call_type: 'Telephonic Call',
        call_version: 'v2',
        // Account info
        account_id: selectedAccount?.id || null,
        account_name: selectedAccount?.name || clinicName || newDoctorName,
        contact_person_name: newDoctorName || selectedAccount?.contacts?.[0]?.name || '',
        contact_phone: newPhone,
        // Purpose
        telephonic_purposes: purposes,
        telephonic_purposes_others: purposesOthers,
        // Products
        products_discussed: [...promoProducts, ...postWorkshopProducts],
        products_higher_interest: [...promoHigherInterest, ...postWorkshopHigher],
        // Complaint
        complaint_products: complaintProducts,
        complaint_closed: complaintClosed,
        // Workshop
        workshop_name: workshopInviteName || postWorkshopName,
        workshop_date: workshopInviteDate || postWorkshopDate,
        workshop_response: workshopInviteResponse,
        // Follow-up
        follow_up_actions: followUpActions,
        follow_up_others: followUpOthers,
        call_experience: String(callExperience || ''),
        // Account creation
        account_created: accountCreated,
        is_lead: accountMode === 'new' && (createAccount === 'no' || !accountCreated),
        // Date & notes
        follow_up_date: getFollowUpDate(),
        personal_notes: personalNotes,
        who_met: 'Telephonic',
        products: [],
      };
      await axios.post(`${API}/sales-calls/v2`, payload, { headers: { Authorization: `Bearer ${token}` } });
      const isLead = accountMode === 'new' && (createAccount === 'no' || !accountCreated);
      setSubmitted(true); // Disable save button immediately — prevents duplicate saves
      toast.success(isLead ? 'Lead saved! 📋' : 'Telephonic call logged! 🎉');
      setTimeout(() => {
        navigate(isLead ? '/leads' : '/dashboard');
      }, 1500);
    } catch (err) {
      const respData = err.response?.data;
      let detail = err.message || 'Failed to save';
      if (respData) {
        if (typeof respData.detail === 'string') detail = respData.detail;
        else if (Array.isArray(respData.detail)) { console.error('Validation errors:', respData.detail); detail = respData.detail.map(e => `${(e.loc||[]).join('.')}: ${e.msg||''}`).join(' | '); }
        else if (respData.detail) detail = JSON.stringify(respData.detail);
      }
      console.error('🔴 FULL ERROR:', JSON.stringify(respData, null, 2));
      toast.error(detail, { duration: 10000 });
    } finally { setSubmitting(false); }
  };

  let step = 0;
  const s = () => ++step;

  return (
    <Layout onLogout={onLogout}>
      <div className="p-4 max-w-2xl mx-auto pb-20">
        {/* Header */}
        <div className="flex items-center gap-3 mb-5">
          <button onClick={() => navigate('/dashboard')} className="p-2 hover:bg-slate-100 rounded-lg">
            <ArrowLeft className="h-5 w-5 text-slate-500" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
              📞 Log a Telephonic Call
            </h1>
            <p className="text-xs text-slate-500 mt-0.5">For both existing accounts and new contacts</p>
          </div>
        </div>

        <div className="space-y-4">

          {/* S1: Date */}
          <Section step={s()} title="Call Date">
            <Input type="date" value={callDate} onChange={e => setCallDate(e.target.value)} className="h-12 rounded-xl max-w-xs" />
          </Section>

          {/* S2: Account Mode */}
          <Section step={s()} title="Who did you call?">
            <div className="grid grid-cols-2 gap-3">
              <Chip label="📋 Existing Account" selected={accountMode === 'existing'} fullWidth
                onClick={() => { setAccountMode('existing'); setNewPhone(''); setNewDoctorName(''); }} />
              <Chip label="➕ New Contact" selected={accountMode === 'new'} fullWidth
                onClick={() => { setAccountMode('new'); setSelectedAccount(null); setAccountSearch(''); }} />
            </div>
          </Section>

          {/* Existing account search */}
          {accountMode === 'existing' && (
            <Section step={s()} title="Select Account">
              <div className="relative" ref={searchRef}>
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input value={accountSearch} onChange={e => { setAccountSearch(e.target.value); setSelectedAccount(null); }}
                  placeholder="Search clinic or doctor..." className="pl-10 h-12 rounded-xl" />
                {searching && <div className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin h-4 w-4 border-2 border-teal-500 border-t-transparent rounded-full" />}
                {showSearchResults && searchResults.length > 0 && (
                  <div className="absolute top-14 left-0 right-0 bg-white rounded-xl border border-slate-200 shadow-xl z-50 max-h-60 overflow-y-auto">
                    {searchResults.map(acc => (
                      <button key={acc.id} type="button" onClick={() => handleSelectAccount(acc)}
                        className="w-full text-left px-4 py-3 hover:bg-teal-50 border-b last:border-0">
                        <p className="font-semibold text-slate-800 text-sm">{acc.name}</p>
                        <p className="text-xs text-slate-400">📍 {acc.city} · {acc.contacts?.[0]?.name}</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {/* Missing fields blocker for existing accounts */}
              {selectedAccount && missingFields.length > 0 && (
                <div className="mt-3 p-4 bg-amber-50 rounded-xl border border-amber-300 space-y-3">
                  <div className="flex items-start gap-2">
                    <span className="text-amber-600 text-lg">⚠️</span>
                    <div>
                      <p className="font-semibold text-amber-800 text-sm">Complete account details before logging call</p>
                      <ul className="mt-1">
                        {missingFields.map(f => <li key={f} className="text-xs text-amber-700">• {f}</li>)}
                      </ul>
                    </div>
                  </div>
                  {missingFields.includes('Clinic Potential') && (
                    <div>
                      <p className="text-xs font-semibold text-slate-700 mb-1">Clinic Potential *</p>
                      <div className="flex gap-2">
                        {['High', 'Medium', 'Low'].map(opt => (
                          <button key={opt} type="button" onClick={() => setInlinePotential(opt)}
                            className={`flex-1 py-2 rounded-xl border-2 text-xs font-bold transition-all ${
                              inlinePotential === opt
                                ? opt === 'High' ? 'bg-emerald-600 text-white border-emerald-600'
                                : opt === 'Medium' ? 'bg-amber-500 text-white border-amber-500'
                                : 'bg-red-500 text-white border-red-500'
                                : 'bg-white text-slate-600 border-slate-200'
                            }`}>{opt}</button>
                        ))}
                      </div>
                    </div>
                  )}
                  {missingFields.includes('City') && (
                    <div>
                      <p className="text-xs font-semibold text-slate-700 mb-1">City *</p>
                      <Input value={inlineCity} onChange={e => setInlineCity(e.target.value)}
                        placeholder="Enter city" className="h-9 rounded-lg text-sm" />
                    </div>
                  )}
                  <button type="button" onClick={handleSaveMissingFields} disabled={savingMissing}
                    className="w-full py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-xl text-sm font-semibold">
                    {savingMissing ? 'Saving...' : 'Save & Continue →'}
                  </button>
                </div>
              )}

              {selectedAccount && (
                <div className="mt-3 p-3 bg-teal-50 rounded-xl border border-teal-200 flex items-center justify-between">
                  <div>
                    <p className="font-bold text-teal-800 text-sm">{selectedAccount.name}</p>
                    <p className="text-xs text-teal-600">📍 {selectedAccount.city} · {selectedAccount.contacts?.length} contact(s)</p>
                  </div>
                  <button type="button" onClick={() => { setSelectedAccount(null); setAccountSearch(''); setContactPersonId(''); }}>
                    <X className="h-4 w-4 text-teal-400" />
                  </button>
                </div>
              )}
            </Section>
          )}

          {/* Contact selection for existing accounts */}
          {accountMode === 'existing' && selectedAccount && (
            <Section step={s()} title="Who did you call?" subtitle="Select the contact you spoke with">
              <div className="space-y-2">
                {(selectedAccount.contacts || []).length === 0 && (
                  <p className="text-sm text-slate-400 text-center py-3">No contacts for this account</p>
                )}
                {(selectedAccount.contacts || []).map(c => (
                  <button key={c.id} type="button" onClick={() => setContactPersonId(c.id)}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left ${
                      contactPersonId === c.id ? 'border-teal-500 bg-teal-50' : 'border-slate-200 hover:border-teal-300'
                    }`}>
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-600 to-emerald-400 flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold">{c.name?.charAt(0) || '?'}</span>
                    </div>
                    <div>
                      <p className="font-semibold text-slate-800 text-sm">Dr. {c.name}</p>
                      <p className="text-xs text-slate-400">{c.designation} · 📞 {c.whatsapp_number}</p>
                      {c.can_influence_decisions && (
                        <span className={`text-xs px-2 py-0.5 rounded-lg font-medium ${
                          c.can_influence_decisions === 'High' ? 'bg-emerald-100 text-emerald-700' :
                          c.can_influence_decisions === 'Medium' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                        }`}>Purchase Influence: {c.can_influence_decisions}</span>
                      )}
                    </div>
                  </button>
                ))}
                <button type="button" onClick={() => setShowNewContactModal(true)}
                  className="flex items-center gap-2 text-sm text-teal-700 font-medium mt-1 p-2 hover:bg-teal-50 rounded-xl w-full">
                  <Plus className="h-4 w-4" /> Add New Contact
                </button>
              </div>
            </Section>
          )}

          {/* New contact entry */}
          {accountMode === 'new' && (
            <Section step={s()} title="New Contact Details"
              subtitle="Phone number checked for duplicates automatically">
              <div className="space-y-3">
                {/* Phone first */}
                <div>
                  <Label className="text-sm font-semibold text-slate-700 mb-1 block">Contact Number *</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input value={newPhone} onChange={e => setNewPhone(e.target.value)}
                      placeholder="Doctor's phone number" className="pl-10 h-12 rounded-xl" />
                    {checkingDuplicate && <div className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin h-4 w-4 border-2 border-teal-500 border-t-transparent rounded-full" />}
                  </div>
                  {/* Duplicate warning */}
                  {duplicateAccount && (
                    <div className="mt-2 p-3 bg-amber-50 border border-amber-300 rounded-xl">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-semibold text-amber-800">Number matches existing account!</p>
                          <p className="text-xs text-amber-700 mt-0.5">{duplicateAccount.name} · {duplicateAccount.city}</p>
                          <button type="button" onClick={() => { setAccountMode('existing'); setSelectedAccount(duplicateAccount); setAccountSearch(duplicateAccount.name); setNewPhone(''); setDuplicateAccount(null); }}
                            className="mt-2 text-xs bg-amber-600 text-white px-3 py-1.5 rounded-lg font-semibold">
                            Use Existing Account Instead
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                {/* Doctor name */}
                <div>
                  <Label className="text-sm font-semibold text-slate-700 mb-1 block">Doctor Name *</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input value={newDoctorName} onChange={e => setNewDoctorName(e.target.value)}
                      placeholder="Dr. Full Name" className="pl-10 h-12 rounded-xl" />
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-semibold text-slate-700 mb-1 block">Email (Optional)</Label>
                  <Input value={newEmail} onChange={e => setNewEmail(e.target.value)}
                    placeholder="doctor@email.com" type="email" className="h-12 rounded-xl" />
                </div>
                <div>
                  <Label className="text-sm font-semibold text-slate-700 mb-1 block">City (Optional)</Label>
                  <Input value={newCity} onChange={e => setNewCity(e.target.value)}
                    placeholder="City name" className="h-12 rounded-xl" />
                </div>
              </div>
            </Section>
          )}

          {/* S3: Purpose */}
          {accountReady && (accountMode === 'new' ? missingFields.length === 0 : true) && (accountMode === 'new' || contactPersonId) && (
            <Section step={s()} title="Purpose of Call" subtitle="Multiple selections allowed">
              <div className="flex flex-col gap-2">
                {PURPOSES.map(p => (
                  <button key={p} type="button"
                    onClick={() => setPurposes(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p])}
                    className={`flex items-center gap-3 w-full text-left px-4 py-3 rounded-xl border-2 text-sm font-semibold transition-all ${
                      purposes.includes(p) ? 'bg-teal-700 text-white border-teal-700' : 'bg-white text-slate-600 border-slate-200 hover:border-teal-400'
                    }`}>
                    {purposes.includes(p) ? <CheckSquare className="h-5 w-5 flex-shrink-0" /> : <Square className="h-5 w-5 flex-shrink-0 text-slate-300" />}
                    {p}
                  </button>
                ))}
              </div>
              {purposes.includes('Others') && (
                <Input value={purposesOthers} onChange={e => setPurposesOthers(e.target.value)}
                  placeholder="Describe the purpose..." className="mt-3 rounded-xl" />
              )}
            </Section>
          )}

          {/* ── PRODUCT PROMOTION ── */}
          {purposesReady && hasPromo && (
            <>
              <Section step={s()} title="🛍️ Product Promotion — Products Discussed">
                <div className="flex flex-wrap gap-2">
                  {allProducts.map(p => (
                    <button key={p.id} type="button"
                      onClick={() => setPromoProducts(prev => prev.includes(p.id) ? prev.filter(x => x !== p.id) : [...prev, p.id])}
                      className={`px-4 py-2.5 rounded-xl border-2 text-sm font-semibold transition-all ${
                        promoProducts.includes(p.id) ? 'bg-teal-700 text-white border-teal-700' : 'bg-white text-slate-600 border-slate-200 hover:border-teal-400'
                      }`}>
                      {p.name}
                    </button>
                  ))}
                </div>
              </Section>

              {promoProducts.length > 0 && (
                <Section step={s()} title="🌟 Products with Higher Interest Expressed">
                  <div className="flex flex-wrap gap-2">
                    {allProducts.map(p => (
                      <button key={p.id} type="button"
                        onClick={() => setPromoHigherInterest(prev => prev.includes(p.id) ? prev.filter(x => x !== p.id) : [...prev, p.id])}
                        className={`px-4 py-2.5 rounded-xl border-2 text-sm font-semibold transition-all ${
                          promoHigherInterest.includes(p.id) ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-slate-600 border-slate-200 hover:border-emerald-400'
                        }`}>
                        {p.name}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-slate-400 mt-2">Leave empty if no specific product had higher interest</p>
                </Section>
              )}
            </>
          )}

          {/* ── COMPLAINT RESOLUTION ── */}
          {purposesReady && hasComplaint && (
            <>
              <Section step={s()} title="⚠️ Complaint Resolution — Products with Complaint">
                <div className="flex flex-wrap gap-2">
                  {allProducts.map(p => (
                    <button key={p.id} type="button"
                      onClick={() => setComplaintProducts(prev => prev.includes(p.id) ? prev.filter(x => x !== p.id) : [...prev, p.id])}
                      className={`px-4 py-2.5 rounded-xl border-2 text-sm font-semibold transition-all ${
                        complaintProducts.includes(p.id) ? 'bg-red-500 text-white border-red-500' : 'bg-white text-slate-600 border-slate-200 hover:border-red-400'
                      }`}>
                      {p.name}
                    </button>
                  ))}
                </div>
              </Section>

              {complaintProducts.length > 0 && (
                <Section step={s()} title="Complaint Closed?">
                  <div className="grid grid-cols-2 gap-3">
                    <Chip label="✅ Yes — Closed" selected={complaintClosed === 'Yes'} onClick={() => setComplaintClosed('Yes')} color="emerald" fullWidth />
                    <Chip label="❌ No — Still Open" selected={complaintClosed === 'No'} onClick={() => setComplaintClosed('No')} color="red" fullWidth />
                  </div>
                </Section>
              )}
            </>
          )}

          {/* ── WEBINAR/WORKSHOP INVITE ── */}
          {purposesReady && hasWorkshopInvite && (
            <Section step={s()} title="🎓 Webinar/Workshop Invite Details">
              <div className="space-y-3">
                <div>
                  <Label className="text-sm font-semibold text-slate-700 mb-1 block">Workshop / Webinar Name *</Label>
                  <Input value={workshopInviteName} onChange={e => setWorkshopInviteName(e.target.value)}
                    placeholder="e.g. Orthodontic Excellence Webinar" className="rounded-xl" />
                </div>
                <div>
                  <Label className="text-sm font-semibold text-slate-700 mb-1 block">Date *</Label>
                  <Input type="date" value={workshopInviteDate} onChange={e => setWorkshopInviteDate(e.target.value)} className="h-12 rounded-xl max-w-xs" />
                </div>
                <div>
                  <Label className="text-sm font-semibold text-slate-700 mb-2 block">Doctor's Response *</Label>
                  <div className="flex flex-col gap-2">
                    {WORKSHOP_RESPONSES.map(r => (
                      <Chip key={r} label={r} selected={workshopInviteResponse === r} fullWidth
                        onClick={() => setWorkshopInviteResponse(r)}
                        color={r === 'Interested' ? 'emerald' : r === 'Not Interested' ? 'red' : 'amber'} />
                    ))}
                  </div>
                </div>
              </div>
            </Section>
          )}

          {/* ── POST WEBINAR FOLLOW-UP ── */}
          {purposesReady && hasPostWorkshop && (
            <>
              <Section step={s()} title="📋 Post Webinar/Workshop Follow-Up Details">
                <div className="space-y-3">
                  <div>
                    <Label className="text-sm font-semibold text-slate-700 mb-1 block">Workshop / Webinar Name *</Label>
                    <Input value={postWorkshopName} onChange={e => setPostWorkshopName(e.target.value)}
                      placeholder="e.g. Orthodontic Excellence Webinar" className="rounded-xl" />
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-slate-700 mb-1 block">Date *</Label>
                    <Input type="date" value={postWorkshopDate} onChange={e => setPostWorkshopDate(e.target.value)} className="h-12 rounded-xl max-w-xs" />
                  </div>
                </div>
              </Section>

              <Section step={s()} title="📦 Post Workshop — Products Discussed">
                <div className="flex flex-wrap gap-2">
                  {allProducts.map(p => (
                    <button key={p.id} type="button"
                      onClick={() => setPostWorkshopProducts(prev => prev.includes(p.id) ? prev.filter(x => x !== p.id) : [...prev, p.id])}
                      className={`px-4 py-2.5 rounded-xl border-2 text-sm font-semibold transition-all ${
                        postWorkshopProducts.includes(p.id) ? 'bg-teal-700 text-white border-teal-700' : 'bg-white text-slate-600 border-slate-200 hover:border-teal-400'
                      }`}>
                      {p.name}
                    </button>
                  ))}
                </div>
              </Section>

              {postWorkshopProducts.length > 0 && (
                <Section step={s()} title="🌟 Products with Higher Interest">
                  <div className="flex flex-wrap gap-2">
                    {allProducts.filter(p => postWorkshopProducts.includes(p.id)).map(p => (
                      <button key={p.id} type="button"
                        onClick={() => setPostWorkshopHigher(prev => prev.includes(p.id) ? prev.filter(x => x !== p.id) : [...prev, p.id])}
                        className={`px-4 py-2.5 rounded-xl border-2 text-sm font-semibold transition-all ${
                          postWorkshopHigher.includes(p.id) ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-slate-600 border-slate-200 hover:border-emerald-400'
                        }`}>
                        {p.name}
                      </button>
                    ))}
                  </div>
                </Section>
              )}
            </>
          )}

          {/* ── FOLLOW-UP ACTIONS (common) ── */}
          {purposesReady && (
            <Section step={s()} title="Follow-Up Actions" subtitle="Multiple selections allowed">
              <Checklist
                options={FOLLOWUP_ACTIONS}
                selected={followUpActions}
                onChange={setFollowUpActions}
                othersText={followUpOthers}
                onOthersChange={setFollowUpOthers}
              />
            </Section>
          )}

          {/* ── CALL EXPERIENCE ── */}
          {purposesReady && (
            <Section step={s()} title="Overall Call Experience">
              <CallExperience value={callExperience} onChange={setCallExperience} />
            </Section>
          )}

          {/* ── CREATE ACCOUNT? (new contacts only) ── */}
          {purposesReady && accountMode === 'new' && !accountCreated && (
            <Section step={s()} title="Do you have enough information to create this as an account?"
              subtitle="You need: Clinic name, Address, Doctor name">
              <div className="grid grid-cols-2 gap-3 mb-4">
                <Chip label="✅ Yes — Create Account" selected={createAccount === 'yes'} fullWidth
                  onClick={() => setCreateAccount('yes')} color="emerald" />
                <Chip label="⏭️ No — Save as Lead" selected={createAccount === 'no'} fullWidth
                  onClick={() => setCreateAccount('no')} color="teal" />
              </div>

              {createAccount === 'yes' && (
                <div className="space-y-3 p-4 bg-teal-50 rounded-xl border border-teal-200">
                  <p className="text-sm font-semibold text-teal-800 mb-3">Quick Account Setup</p>
                  <div>
                    <Label className="text-xs text-slate-600 mb-1 block">Clinic Name *</Label>
                    <div className="relative">
                      <Input value={clinicName} onChange={e => setClinicName(e.target.value)}
                        placeholder="Clinic / Hospital name" className="rounded-xl" />
                      {checkingClinicDuplicate && <div className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin h-4 w-4 border-2 border-teal-500 border-t-transparent rounded-full" />}
                    </div>
                    {duplicateClinic && (
                      <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-xl space-y-1">
                        <p className="text-xs font-semibold text-amber-800">⚠️ Similar clinic already exists!</p>
                        <p className="text-xs text-amber-700">{duplicateClinic.name} · {duplicateClinic.city}</p>
                        <button type="button"
                          onClick={() => { setAccountMode('existing'); setSelectedAccount(duplicateClinic); setAccountSearch(duplicateClinic.name); setDuplicateClinic(null); setCreateAccount(''); }}
                          className="text-xs bg-amber-600 text-white px-3 py-1.5 rounded-lg font-semibold">
                          Switch to Existing Account
                        </button>
                      </div>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600 mb-1 block">Address *</Label>
                    <Input value={clinicAddress} onChange={e => setClinicAddress(e.target.value)}
                      placeholder="Full address" className="rounded-xl" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600 mb-1 block">City</Label>
                    <Input value={clinicCity || newCity} onChange={e => setClinicCity(e.target.value)}
                      placeholder="City" className="rounded-xl" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600 mb-1 block">Pincode</Label>
                    <Input value={clinicPincode} onChange={e => setClinicPincode(e.target.value)}
                      placeholder="6-digit pincode" className="rounded-xl" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600 mb-2 block">Clinic Potential</Label>
                    <div className="flex gap-2">
                      {['High', 'Medium', 'Low'].map(opt => (
                        <Chip key={opt} label={opt} selected={clinicPatientInflow === opt}
                          onClick={() => setClinicPatientInflow(opt)}
                          color={opt === 'High' ? 'emerald' : opt === 'Medium' ? 'amber' : 'red'} />
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600 mb-2 block">Competitor Brands</Label>
                    <div className="flex flex-wrap gap-2">
                      {COMPETITOR_OPTIONS.map(opt => (
                        <button key={opt} type="button"
                          onClick={() => setClinicCompetitors(prev => prev.includes(opt) ? prev.filter(x => x !== opt) : [...prev, opt])}
                          className={`text-xs px-3 py-2 rounded-xl border-2 font-medium transition-all ${
                            clinicCompetitors.includes(opt) ? 'bg-teal-700 text-white border-teal-700' : 'bg-white text-slate-600 border-slate-200 hover:border-teal-400'
                          }`}>
                          {opt}
                        </button>
                      ))}
                    </div>
                  </div>
                  <p className="text-xs text-teal-600 mt-1">
                    Contact: Dr. {newDoctorName} · {newPhone} will be pre-filled as contact.
                  </p>
                  <p className="text-xs text-amber-600 mt-1">
                    ⚠️ Clinic Potential is optional now but will be required before the next call.
                  </p>
                  <Button onClick={handleCreateAccountInline} disabled={creatingAccount}
                    className="w-full bg-gradient-to-r from-teal-700 to-emerald-500 text-white rounded-xl">
                    {creatingAccount ? 'Creating...' : '✅ Create Account & Continue'}
                  </Button>
                </div>
              )}
            </Section>
          )}

          {accountCreated && (
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center gap-2">
              <span className="text-emerald-600 text-lg">✅</span>
              <p className="text-sm font-semibold text-emerald-800">Account created! Call will be linked to the new account.</p>
            </div>
          )}

          {/* ── PERSONAL NOTES ── */}
          {purposesReady && (
            <Section step={s()} title="Personal Notes" subtitle="Optional — only visible to you and your manager">
              <textarea value={personalNotes} onChange={e => setPersonalNotes(e.target.value)}
                placeholder={`Examples:\n• "Doctor is interested in a demo next week"\n• "Call again Tuesday morning — more free time then"`}
                rows={3}
                className="w-full rounded-xl border border-slate-200 focus:border-teal-500 focus:outline-none px-4 py-3 text-sm resize-none" />
            </Section>
          )}

          {/* ── FOLLOW-UP DATE ── */}
          {purposesReady && (
            <Section step={s()} title="Follow-Up Date *">
              <div className="flex flex-col gap-2">
                {FOLLOWUP_DATE_OPTIONS.map(opt => (
                  <Chip key={opt} label={opt} selected={followUpDate === opt} fullWidth
                    onClick={() => setFollowUpDate(opt)}
                    color={opt === 'Assign to Representative' ? 'amber' : 'teal'} />
                ))}
              </div>
              {followUpDate === 'Custom Date' && (
                <Input type="date" value={customDate} onChange={e => setCustomDate(e.target.value)}
                  min={new Date().toISOString().slice(0, 10)} className="h-12 rounded-xl mt-3 max-w-xs" />
              )}
            </Section>
          )}

          {/* ── SAVE ── */}
          {followUpDate && (followUpDate !== 'Custom Date' || customDate) && (
            <div className="flex gap-3 pt-2 pb-4">
              <Button onClick={handleSubmit} disabled={submitting || submitted}
                className="flex-1 h-14 bg-gradient-to-r from-teal-700 to-emerald-500 text-white font-bold text-base rounded-2xl shadow-lg active:scale-[0.98]">
                <Save className="mr-2 h-5 w-5" />
                {submitted ? '✅ Saved!' : submitting ? 'Saving...' : 'Save Telephonic Call'}
              </Button>
              <Button variant="outline" onClick={() => navigate('/dashboard')} className="h-14 rounded-2xl px-6">
                Cancel
              </Button>
            </div>
          )}

        </div>
      </div>
      <Dialog open={showNewContactModal} onOpenChange={setShowNewContactModal}>
        <DialogContent className="max-w-md mx-4">
          <DialogHeader><DialogTitle>Add New Contact</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label className="text-xs font-semibold text-slate-600 mb-1 block">Full Name *</Label>
              <Input value={newContactForm.name} onChange={e => setNewContactForm(p => ({...p, name: e.target.value}))} placeholder="Dr. Full Name" className="rounded-xl" /></div>
            <div><Label className="text-xs font-semibold text-slate-600 mb-1 block">Designation *</Label>
              <select value={newContactForm.designation} onChange={e => setNewContactForm(p => ({...p, designation: e.target.value}))} className="w-full h-10 rounded-xl border border-slate-200 px-3 text-sm focus:outline-none focus:border-teal-500">
                <option value="">Select...</option>
                {['Clinic Owner (Orthodontist)','Clinic Owner (Non-Orthodontist)','Consultant (Orthodontist)','Consultant (Non-Orthodontist)','Others'].map(d => <option key={d} value={d}>{d}</option>)}
              </select></div>
            <div><Label className="text-xs font-semibold text-slate-600 mb-1 block">Phone *</Label>
              <Input value={newContactForm.whatsapp_number} onChange={e => setNewContactForm(p => ({...p, whatsapp_number: e.target.value}))} placeholder="Mobile number" className="rounded-xl" /></div>
            <div><Label className="text-xs font-semibold text-slate-600 mb-1 block">Email (Optional)</Label>
              <Input value={newContactForm.email} onChange={e => setNewContactForm(p => ({...p, email: e.target.value}))} placeholder="email@example.com" type="email" className="rounded-xl" /></div>
            <div><Label className="text-xs font-semibold text-slate-600 mb-2 block">Influence on Purchase Decision? *</Label>
              <div className="flex gap-2">
                {['High','Medium','Low'].map(opt => (
                  <button key={opt} type="button" onClick={() => setNewContactForm(p => ({...p, can_influence_decisions: opt}))}
                    className={`flex-1 py-2.5 rounded-xl border-2 text-sm font-semibold transition-all ${newContactForm.can_influence_decisions === opt ? opt==='High' ? 'bg-emerald-600 text-white border-emerald-600' : opt==='Medium' ? 'bg-amber-500 text-white border-amber-500' : 'bg-red-500 text-white border-red-500' : 'bg-white text-slate-600 border-slate-200'}`}>{opt}</button>
                ))}
              </div></div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowNewContactModal(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleAddNewContact} disabled={addingContact} className="flex-1 bg-gradient-to-r from-teal-700 to-emerald-500 text-white rounded-xl">
                {addingContact ? 'Adding...' : 'Add Contact'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </Layout>
  );
};

export default CreateTelephonicCall;
